import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingState } from '../../types';
import serviceService, { Service } from '../../services/serviceService';

interface ServiceState extends LoadingState {
  services: Service[];
  selectedService: Service | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  searchQuery: string;
}

const initialState: ServiceState = {
  services: [],
  selectedService: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasMore: false,
  },
  searchQuery: '',
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchServices = createAsyncThunk(
  'services/fetchServices',
  async (params: { page?: number; limit?: number; search?: string; append?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = '', append = false } = params;
      const response = await serviceService.getActiveServices({ page, limit, search });
      return { ...response, append };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch services');
    }
  }
);

export const createService = createAsyncThunk(
  'services/createService',
  async (data: { name: string; status: 'active' | 'inactive' }, { rejectWithValue }) => {
    try {
      const response = await serviceService.createService(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create service');
    }
  }
);

const serviceSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedService: (state, action: PayloadAction<Service | null>) => {
      state.selectedService = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.services = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Services
      .addCase(fetchServices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, append } = action.payload;
        
        if (append) {
          state.services.push(...data.data as any);
        } else {
          state.services = data.data as any;
        }
        
        state.pagination = {
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          pages: data.pagination.pages || data.pagination.totalPages,
          hasMore: data.pagination.page < (data.pagination.pages || data.pagination.totalPages),
        };
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Service
      .addCase(createService.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.isLoading = false;
        const newService = action.payload;
        
        // Create a properly typed object for WritableDraft
        const serviceToAdd: Service = {
          _id: newService._id,
          name: newService.name,
          slug: newService.slug,
          status: newService.status,
          isDeleted: newService.isDeleted,
          createdBy: newService.createdBy,
          createdAt: newService.createdAt,
          updatedAt: newService.updatedAt
        };
        
        state.services.unshift(serviceToAdd as any);
        state.pagination.total += 1;
      })
      .addCase(createService.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedService,
  setSearchQuery,
  resetPagination,
} = serviceSlice.actions;

export default serviceSlice.reducer;
